import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Monitor, Smartphone, CheckCircle, ArrowRight, ShoppingCart } from 'lucide-react';
import SEOMeta, { BOOK_META } from '../../../components/SEOMeta';

const STRIPE_PAYMENT_LINK = 'https://buy.stripe.com/6oUcN4etP3qP4kZfC38so01';
const BOOK_COVER_URL = 'https://customer-assets.emergentagent.com/job_30b1d3cb-b799-4691-ba56-7e824b5598c4/artifacts/p185kuox_font%20cover%20jpg.jpg';

const BookHubPage = () => {
  const benefits = [
    'Master cultural intelligence for global leadership',
    'Navigate identity without compromise',
    'Build authentic connections across cultures',
    'Lead diverse teams with confidence',
    'Transform cultural friction into creative advantage'
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Buy Access',
      description: 'One-time payment of £9.99. No subscriptions, no hidden fees.',
      icon: ShoppingCart
    },
    {
      step: 2,
      title: 'Sign In',
      description: 'Create your account and access your library on any device.',
      icon: Monitor
    },
    {
      step: 3,
      title: 'Read Anywhere',
      description: 'Web reader, iOS, or Android. Your progress syncs automatically.',
      icon: BookOpen
    }
  ];

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <SEOMeta 
        url="/books/cultural-fluency-without-compromise"
        title={BOOK_META.title}
        description={BOOK_META.description}
        image={BOOK_META.image}
        imageAlt={BOOK_META.imageAlt}
        type="book"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2c3e50] to-[#1a252f] text-white py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Book Cover */}
            <div className="flex justify-center">
              <div className="rounded-lg shadow-2xl max-w-xs overflow-hidden">
                <img 
                  src={BOOK_COVER_URL}
                  alt="Cultural Fluency Without Compromise book cover"
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Book Info */}
            <div>
              <p className="text-[#d4af37] text-sm tracking-wider mb-4">NOW AVAILABLE</p>
              <h2 className="text-4xl md:text-5xl font-bold mb-2 font-display">CULTURAL FLUENCY</h2>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 font-display">WITHOUT COMPROMISE</h2>
              <p className="text-xl text-white/80 mb-2">Thriving Across Cultures with Presence and Integrity</p>
              <p className="text-lg text-[#d4af37] mb-6">by PROMISE MONDAY</p>
              <p className="text-sm text-white/60 mb-8">ISBN (eBook): 978-1-918265-02-6</p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/books/cultural-fluency-without-compromise/read"
                  className="inline-flex items-center gap-2 bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors"
                  data-testid="read-on-web-btn"
                >
                  <BookOpen size={20} />
                  Read on Web
                </Link>
                <Link
                  to="/books/cultural-fluency-without-compromise/apps"
                  className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
                  data-testid="get-app-btn"
                >
                  <Smartphone size={20} />
                  Get the App
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Gain */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-[#2c3e50] text-center mb-12 font-display">
            What You&apos;ll Gain
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                <CheckCircle className="w-5 h-5 text-[#d4af37] flex-shrink-0 mt-0.5" />
                <p className="text-[#2c3e50]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-[#2c3e50] text-center mb-4 font-display">
            How It Works
          </h2>
          <p className="text-center text-[#5a6c7d] mb-12">
            Get reading in three simple steps
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-[#d4af37]" />
                </div>
                <div className="text-sm text-[#d4af37] font-semibold mb-2">Step {item.step}</div>
                <h3 className="text-xl font-bold text-[#2c3e50] mb-2">{item.title}</h3>
                <p className="text-[#5a6c7d]">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Purchase CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#2c3e50] to-[#1a252f]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-display">
            Start Reading Today
          </h2>
          <p className="text-white/80 mb-8">
            One-time purchase. Lifetime access. Read on any device.
          </p>
          <a
            href={STRIPE_PAYMENT_LINK}
            className="inline-flex items-center gap-3 bg-[#d4af37] text-[#2c3e50] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#e5c349] transition-colors"
            data-testid="buy-ebook-btn"
          >
            <ShoppingCart size={24} />
            Buy eBook Access – £9.99
          </a>
          <p className="text-white/50 text-sm mt-4">
            Secure checkout powered by Stripe
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a252f] text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-bold mb-4 text-[#d4af37]">Read Anywhere</h3>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/books/cultural-fluency-without-compromise/read" className="hover:text-white">Web Reader</Link></li>
                <li><Link to="/books/cultural-fluency-without-compromise/apps" className="hover:text-white">Apps Hub</Link></li>
                <li><Link to="/books/cultural-fluency-without-compromise/ios" className="hover:text-white">iOS</Link></li>
                <li><Link to="/books/cultural-fluency-without-compromise/android" className="hover:text-white">Android</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-[#d4af37]">Support</h3>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/support" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-[#d4af37]">Legal</h3>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-sm text-white/50">
              ISBN (eBook): 978-1-918265-02-6
            </p>
            <p className="text-sm text-white/50 mt-2">
              © {new Date().getFullYear()} Third Culture Leadership. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BookHubPage;
